import { ACCENT, BG, BORDER, CARD, DARK, DIM, MID, RED } from "../theme";
import { PAYS_PEOPLE_LABEL, PRESETS, PRESET_KEYS } from "../businessPreset";
import { useState } from "react";

// ─── BUSINESS TYPE PANEL ──────────────────────────────────────────────────────
// A small panel where the owner picks how their costs are shown: Trades, Services with a team or Solo services.
// "Services with a team" also asks whether they pay people per job, which decides if a job with no labor is
// marked incomplete. Nothing changes the margin math, only labels and that one completeness rule.
// onSave(key, paysPeople) returns a promise of { ok, message }.

const FONT = "'DM Sans',sans-serif";

function BusinessTypePanel({ businessType, onSave, onClose }) {
  const [key, setKey] = useState(businessType.key);
  const [paysPeople, setPaysPeople] = useState(!!businessType.paysPeople);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function save() {
    setSaving(true);
    setMessage("");
    const result = await onSave(key, paysPeople);
    setSaving(false);
    if (result && result.ok) onClose();
    else setMessage((result && result.message) || "We couldn't save that. Please try again in a moment.");
  }

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="business-type-title" className="r-overlay"
      onKeyDown={e => e.key === "Escape" && onClose()} onClick={onClose}
      style={{ position:"fixed", inset:0, background:"rgba(44,36,22,0.45)", zIndex:600, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
      <div className="r-sheet" onClick={e => e.stopPropagation()}
        style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:8, width:"100%", maxWidth:520, maxHeight:"90vh", overflowY:"auto", padding:"26px 30px", boxShadow:"0 20px 60px rgba(44,36,22,0.2)" }}>
        <h2 id="business-type-title" style={{ fontFamily:"'Lora',serif", fontSize:18, fontWeight:500, color:DARK, marginBottom:6 }}>Business type</h2>
        <p style={{ fontFamily:FONT, fontSize:13, color:MID, lineHeight:1.6, marginBottom:18 }}>
          This changes how costs are named and when a job counts as incomplete. Your profit and margin numbers stay the same.
        </p>
        <fieldset style={{ border:"none", padding:0, margin:0 }}>
          <legend style={{ position:"absolute", left:-9999 }}>Business type</legend>
          {PRESET_KEYS.map(k => (
            <label key={k} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"10px 12px", marginBottom:8, borderRadius:6, cursor:"pointer", border:`1px solid ${key === k ? ACCENT : BORDER}`, background:key === k ? BG : CARD }}>
              <input type="radio" name="business-type" value={k} checked={key === k} onChange={() => setKey(k)} autoFocus={k === businessType.key} style={{ marginTop:3 }} />
              <span>
                <span style={{ display:"block", fontFamily:FONT, fontSize:14, fontWeight:500, color:DARK }}>{PRESETS[k].name}</span>
                <span style={{ display:"block", fontFamily:FONT, fontSize:12, color:MID, lineHeight:1.5, marginTop:2 }}>{PRESETS[k].description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {key === "team" && (
          <label style={{ display:"flex", gap:10, alignItems:"flex-start", margin:"4px 2px 8px", cursor:"pointer", fontFamily:FONT, fontSize:13, color:DARK }}>
            <input type="checkbox" checked={paysPeople} onChange={e => setPaysPeople(e.target.checked)} style={{ marginTop:3 }} />
            <span>
              {PAYS_PEOPLE_LABEL}
              <span style={{ display:"block", fontSize:12, color:DIM, marginTop:2 }}>If you check this, a job with no contractor costs is marked incomplete.</span>
            </span>
          </label>
        )}
        {message && <p role="alert" style={{ fontFamily:FONT, fontSize:12, color:RED, margin:"8px 0" }}>{message}</p>}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:16 }}>
          <button type="button" className="btn" onClick={onClose} disabled={saving} style={{ fontSize:12, padding:"7px 16px" }}>Cancel</button>
          <button type="button" className="btn act" onClick={save} disabled={saving} style={{ fontSize:12, padding:"7px 18px" }}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

export { BusinessTypePanel };
